import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { Card } from "ui/Card";
import { collectionApi } from "lib/api";
import { collectionQueryKeys } from "components/Collections/useCollections";
import { getCollectionUiStrings, resolveCollectionLocale } from "i18n/collectionUi";

interface LibraryCollectionViewerProps {
  collectionId: number;
  enabled?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

const LibraryCollectionViewer: React.FC<LibraryCollectionViewerProps> = ({
  collectionId,
  enabled = true,
}) => {
  const ui = getCollectionUiStrings(resolveCollectionLocale());
  const itemsQuery = useQuery({
    queryKey: collectionQueryKeys.collectionItems(collectionId),
    queryFn: () => collectionApi.getItems(collectionId),
    enabled: enabled && Number.isFinite(collectionId) && collectionId > 0,
  });

  const parsedItems = useMemo(() => {
    const response = itemsQuery.data;
    const items = Array.isArray(response?.items) ? response.items : [];
    const relations = Array.isArray(response?.item_relations) ? response.item_relations : [];

    const mediaItems: Array<{
      id: number;
      previewUrl: string;
      mediaType: string;
      altText: string;
    }> = [];
    const urlItems: Array<{
      id: number;
      url: string;
      title: string;
      description?: string;
      imageUrl?: string;
      siteName?: string;
    }> = [];

    items.forEach((item, index) => {
      const relation = relations[index];
      if (!relation || !isRecord(item)) {
        return;
      }

      if (relation.related_type === "media_asset") {
        const id = Number.parseInt(String(item.id ?? ""), 10);
        const previewUrl =
          (typeof item.signed_url === "string" && item.signed_url) ||
          (typeof item.url === "string" && item.url) ||
          "";
        if (!Number.isFinite(id) || id <= 0 || previewUrl.length === 0) {
          return;
        }
        mediaItems.push({
          id,
          previewUrl,
          mediaType: typeof item.media_type === "string" ? item.media_type : "image",
          altText:
            (typeof item.file_name === "string" && item.file_name) ||
            `${ui.mediaItemLabel} ${mediaItems.length + 1}`,
        });
        return;
      }

      if (relation.related_type === "url_metadata") {
        const id = Number.parseInt(String(item.id ?? ""), 10);
        const url = typeof item.url === "string" ? item.url : "";
        if (!Number.isFinite(id) || id <= 0 || url.length === 0) {
          return;
        }
        urlItems.push({
          id,
          url,
          title:
            (typeof item.title === "string" && item.title) ||
            (typeof item.site_name === "string" && item.site_name) ||
            url,
          description: typeof item.description === "string" ? item.description : undefined,
          imageUrl: typeof item.image_url === "string" ? item.image_url : undefined,
          siteName: typeof item.site_name === "string" ? item.site_name : undefined,
        });
      }
    });

    return { mediaItems, urlItems };
  }, [itemsQuery.data, ui.mediaItemLabel]);

  if (itemsQuery.isPending) {
    return (
      <div className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {ui.libraryViewerLoading}
      </div>
    );
  }

  if (itemsQuery.isError) {
    return (
      <div className="text-sm text-warning-red dark:text-warning-red-dark" role="alert">
        {ui.libraryViewerError}
      </div>
    );
  }

  if (parsedItems.mediaItems.length === 0 && parsedItems.urlItems.length === 0) {
    return (
      <div className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {ui.libraryViewerEmpty}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {parsedItems.mediaItems.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {parsedItems.mediaItems.map((item) => (
            <div
              key={item.id}
              className="overflow-hidden rounded-2xl border border-eoe-border-main bg-eoe-bg-modal dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal"
            >
              {item.mediaType === "video" ? (
                <video
                  src={item.previewUrl}
                  className="aspect-square w-full object-cover"
                  muted
                  controls
                  playsInline
                />
              ) : (
                <img
                  src={item.previewUrl}
                  alt={item.altText}
                  className="aspect-square w-full object-cover"
                />
              )}
            </div>
          ))}
        </div>
      ) : null}

      {parsedItems.urlItems.length > 0 ? (
        <div className="space-y-3">
          {parsedItems.urlItems.map((item) => (
            <Card key={item.id} padding="sm" className="overflow-hidden">
              <div className="flex flex-col gap-3 sm:flex-row">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="h-24 w-full rounded-lg object-cover sm:w-40"
                    referrerPolicy="no-referrer"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {item.title}
                  </div>
                  {item.siteName ? (
                    <div className="mt-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {item.siteName}
                    </div>
                  ) : null}
                  {item.description ? (
                    <p className="mt-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {item.description}
                    </p>
                  ) : null}
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary-action-text hover:text-primary-action-text-hover"
                  >
                    <ExternalLink className="size-4" aria-hidden />
                    {ui.openLink}
                  </a>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default LibraryCollectionViewer;
